import type { AccessRequirementEnum, CreateOrganizationInput } from '@alice-financial/alice-sdk'
import { useCreateOrganization, useCurrentUserQuery } from '@alice-financial/alice-sdk'
import {
  Box,
  Button,
  Divider,
  ExternalLink,
  Grid,
  Link,
  TextField,
  Typography,
} from '@alice-financial/pretext-ui'
import * as React from 'react'
import { useForm, type FieldErrors, type UseFormRegister } from 'react-hook-form'
import { Navigate, useSearchParams } from 'react-router-dom'
import {
  OrganizationDetailsInputs,
  PayrollSelectionInputs,
  type OrganizationFormFields,
} from './IntakeFormSlides'
import { ReferralInputs, type ReferralFormFields } from './ReferralSlide'

export interface OrganizationFormData extends OrganizationFormFields, ReferralFormFields {
  firstName: string
  lastName: string
  workEmail: string
  phoneNumber: string
  // New fields for broker/consultant "Other" option
  brokerName: string
}

export interface OrganizationIntakeFormProps {
  onSuccess?: (organizationId: number, access: AccessRequirementEnum | undefined) => void
  onError?: (error: Error) => void
  isBrokerView?: boolean
}

// Welcome Slide Component
interface WelcomeHeaderProps {
  isBrokerView?: boolean
}

const WelcomeHeader: React.FC<WelcomeHeaderProps> = ({ isBrokerView }) => {
  const [searchParams] = useSearchParams()

  // Fade in when component mounts
  if (searchParams.has('step')) return <Navigate to={{ search: '' }} replace />

  return (
    <>
      <Typography
        variant="h1"
        color="primary"
        fontFamily='"Georgia", "Times New Roman", serif'
        fontSize={{ xs: '2.5rem', md: '3.5rem' }}
      >
        Welcome to Alice!
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {isBrokerView
          ? "Let's get your client set up with commuter benefits."
          : "Let's get your company set up with commuter benefits."}
      </Typography>
    </>
  )
}

// Email Input Slide Component
interface EmailInputSlideProps {
  register: UseFormRegister<OrganizationFormData>
  errors: FieldErrors<OrganizationFormData>
}

const EmailInput: React.FC<EmailInputSlideProps> = ({ register, errors }) => {
  return (
    <TextField
      {...register('workEmail', {
        required: 'Work email is required',
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Invalid email address',
        },
      })}
      label="Work email"
      placeholder="you@company.com"
      type="email"
      fullWidth
      error={!!errors.workEmail}
      helperText={errors.workEmail?.message || 'This will be your login email'}
    />
  )
}

// Name Input Slide Component
interface NameInputSlideProps {
  register: UseFormRegister<OrganizationFormData>
  errors: FieldErrors<OrganizationFormData>
  isBrokerView?: boolean
}

const NameInputs: React.FC<NameInputSlideProps> = ({ register, errors }) => {
  return (
    <>
      <Grid container spacing={3} width="100%">
        <Grid size={6}>
          <TextField
            {...register('firstName', { required: 'First name is required' })}
            label="First name"
            fullWidth
            error={!!errors.firstName}
            helperText={errors.firstName?.message}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            {...register('lastName', { required: 'Last name is required' })}
            label="Last name"
            fullWidth
            error={!!errors.lastName}
            helperText={errors.lastName?.message}
          />
        </Grid>
      </Grid>
    </>
  )
}

// Footer Component
const IntakeFormFooter: React.FC = () => {
  return (
    <Box mt={4}>
      <Typography variant="body2" gutterBottom>
        Need a refresher? Here&apos;s an{' '}
        <ExternalLink
          href="https://www.loom.com/share/4edd36d80e584dd9894fa870ed1b7fa5?sid=8165126a-99e9-4890-9fc7-0ccaa07b7feb"
          color="primary"
        >
          overview of Alice
        </ExternalLink>
      </Typography>
      <Typography variant="body2">
        Already signed up your company?{' '}
        <Link to="/manage" color="primary">
          View your admin dashboard
        </Link>
      </Typography>
    </Box>
  )
}

export const OrganizationIntakeForm: React.FC<OrganizationIntakeFormProps> = ({
  onSuccess,
  onError,
  isBrokerView,
}) => {
  const { data: currentUserData } = useCurrentUserQuery()
  const currentUser = currentUserData?.currentUser
  const { mutateAsync: createOrganization } = useCreateOrganization()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm<OrganizationFormData>({
    mode: 'onChange',
    defaultValues: {
      organizationName: '',
      legalName: '',
      payrollPlatform: '',
      firstName: currentUser?.firstName || '',
      lastName: currentUser?.lastName || '',
      workEmail: currentUser?.email || '',
      // Referral fields
      referralPath: '',
      wordOfMouthFirst: '',
      wordOfMouthLast: '',
      wordOfMouthCompany: '',
      wordOfMouthEmail: '',
      brokerEmail: '',
      brokerName: '',
      partnerExecutiveId: '',
      otherNote: '',
    },
  })

  // Watch form values
  const watchedValues = watch()

  const onSubmit = async (data: OrganizationFormData) => {
    try {
      // Determine leadName based on broker/consultant selection
      let leadName: string | undefined
      if (data.partnerExecutiveId) {
        if (data.partnerExecutiveId === 'Other') {
          // Concatenate name and email for "Other" option
          leadName = `${data.brokerName} ${data.brokerEmail}`.trim()
        } else {
          // Use the selected partner executive ID
          leadName = data.partnerExecutiveId
        }
      } else if (data.brokerName && data.brokerEmail) {
        // If no partner executive selected but name and email provided (when no executives available)
        leadName = `${data.brokerName} ${data.brokerEmail}`.trim()
      }

      const input: CreateOrganizationInput = {
        name: data.organizationName,
        legalName: data.legalName,
        defaultPayrollPlatformCode: data.payrollPlatform,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.workEmail,
        // Include leadName if determined
        ...(leadName && { leadName }),
      }

      const result = await createOrganization({ input })

      if (result.createOrganization?.inputErrors?.length) {
        // Handle validation errors from the server
        result.createOrganization.inputErrors.forEach((error: { argument: string; message: string }) => {
          const fieldName = error.argument as keyof OrganizationFormData
          setError(fieldName, { message: error.message })
        })
      } else if (result.createOrganization?.orgId) {
        // Success - organization was created
        const orgId = result.createOrganization.orgId
        const access = result.createOrganization.access?.orgAdminDashboard
        console.log('Organization created successfully:', result.createOrganization.orgId)
        onSuccess?.(orgId, access)
      }
    } catch (error) {
      console.error('Error creating organization:', error)
      const errorObj = error instanceof Error ? error : new Error('Unknown error occurred')
      onError?.(errorObj)
    }
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container maxWidth="sm" spacing={4}>
          <Grid>
            <WelcomeHeader isBrokerView={isBrokerView} />
          </Grid>
          <Box p={2} sx={(theme) => ({ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 })}>
            <Grid container maxWidth="sm" spacing={4}>
              <Grid container spacing={2}>
                <Grid>
                  <Typography variant="h2">
                    {isBrokerView ? "Tell us about your client's organization" : 'Company info'}
                  </Typography>
                  <Typography variant="body2">
                    If there are multiple legal entities, you may enter any one of them.
                  </Typography>
                </Grid>

                <Grid container spacing={2}>
                  <OrganizationDetailsInputs
                    register={register}
                    errors={errors}
                    isBrokerView={isBrokerView}
                  />
                  <PayrollSelectionInputs register={register} errors={errors} isBrokerView={isBrokerView} />
                </Grid>
              </Grid>
              <Divider sx={{ mt: 1, width: '100%' }} />
              <Grid container spacing={2}>
                <Typography variant="h3" sx={(theme) => ({ color: theme.palette.primary.main })}>
                  Your contact info
                </Typography>
                <NameInputs register={register} errors={errors} isBrokerView={isBrokerView} />
                <EmailInput register={register} errors={errors} />
              </Grid>
              {!isBrokerView && (
                <ReferralInputs
                  register={register}
                  errors={errors}
                  watch={watch}
                  payrollPlatform={watchedValues.payrollPlatform}
                />
              )}
              <Grid>
                <Typography variant="caption" gutterBottom display="block">
                  By clicking &quot;Submit&quot; you agree to receiving follow-up messages to finish
                  onboarding your company to Alice.
                </Typography>
                <Button type="submit" variant="contained" size="large">
                  Submit
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </form>
      <IntakeFormFooter />
    </Box>
  )
}
